<div class="detail-container" *ngIf="!isLoading && spot">

  <!-- Satellite image hero -->
  <div class="map-hero">
    <div class="map-hero-cell" *ngIf="tacticalImageUrl" (click)="openLightbox(tacticalImageUrl, 'Tactical')" title="Click to enlarge">
      <img [src]="tacticalImageUrl" alt="Tactical map" class="map-hero-img" />
      <span class="map-hero-label">Tactical</span>
    </div>
    <div class="map-hero-cell map-hero-cell--placeholder" *ngIf="!tacticalImageUrl">
      <span>No tactical image</span>
    </div>
    <div class="map-hero-cell" *ngIf="contextImageUrl" (click)="openLightbox(contextImageUrl, 'Context')" title="Click to enlarge">
      <img [src]="contextImageUrl" alt="Context map" class="map-hero-img" />
      <span class="map-hero-label">Context</span>
    </div>
    <div class="map-hero-cell map-hero-cell--placeholder" *ngIf="!contextImageUrl">
      <span>No context image</span>
    </div>
  </div>

  <section class="spot-data-panel">

    <app-back-link link="/spots" label="Back to Fishing Spots"></app-back-link>

    <!-- Name (editable) -->
    <div class="spot-identity">
      <div class="spot-name-row">
        <ng-container *ngIf="editingField !== 'name'; else editName">
          <h2 class="spot-title">{{ spot.name || ('Spot #' + spot.id) }}</h2>
          <button class="edit-btn" (click)="startEdit('name', spot.name)" title="Edit name">✎</button>
        </ng-container>
        <ng-template #editName>
          <input class="edit-input edit-input--title" [(ngModel)]="editValue" placeholder="Enter spot name" />
          <button class="save-btn" (click)="saveEdit('name')">Save</button>
          <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
        </ng-template>
      </div>

      <!-- Location (body of water) (editable) -->
      <div class="spot-location-row">
        <ng-container *ngIf="editingField !== 'location'; else editLocation">
          <span class="spot-location">{{ spot.location || 'No location set' }}</span>
          <button class="edit-btn" (click)="startEdit('location', spot.location)" title="Edit location">✎</button>
        </ng-container>
        <ng-template #editLocation>
          <input class="edit-input" [(ngModel)]="editValue" placeholder="e.g. Back River, Whitehall Creek" />
          <button class="save-btn" (click)="saveEdit('location')">Save</button>
          <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
        </ng-template>
      </div>
    </div>

    <!-- Mapbox map -->
    <div id="spot-map" class="spot-map"></div>

    <!-- Map controls -->
    <div class="map-controls">
      <div class="map-center-controls">
        <ng-container *ngIf="!isEditMode">
          <button mat-stroked-button class="edit-loc-btn" (click)="startEditCenter()">Edit Center</button>
        </ng-container>
        <ng-container *ngIf="isEditMode">
          <button mat-stroked-button class="save-loc-btn" (click)="saveCenter()" [disabled]="isSavingLocation">
            {{ isSavingLocation ? 'Saving…' : 'Save Center' }}
          </button>
          <button mat-stroked-button class="cancel-loc-btn" (click)="cancelEditCenter()" [disabled]="isSavingLocation">Cancel</button>
          <span class="edit-hint">Drag the red pin to the correct location</span>
        </ng-container>
      </div>
      <div class="map-radius-controls">
        <label class="radius-label">Radius</label>
        <input class="radius-input" type="number" min="10" max="500" [(ngModel)]="radiusValue" />
        <span class="radius-unit">m</span>
        <button mat-stroked-button class="save-radius-btn" (click)="saveRadius()" [disabled]="isSavingRadius">
          {{ isSavingRadius ? 'Saving…' : 'Save Radius' }}
        </button>
      </div>
    </div>

    <!-- Coords -->
    <dl class="spot-metadata">
      <dt>Centroid</dt>
      <dd>{{ spot.center_lat | number:'1.5-5' }}, {{ spot.center_lng | number:'1.5-5' }}</dd>
      <dt>Visits</dt>
      <dd>{{ spot.visit_count }} ({{ formatDuration(spot.total_minutes) }} total)</dd>
      <dt>Catches</dt>
      <dd>{{ spot.catch_count }}</dd>
    </dl>

    <!-- Notes -->
    <h3 class="section-label">Angler Notes</h3>
    <app-resizable-textarea
      [(ngModel)]="notes"
      placeholder="Add context: structure type, bait that works, conditions that fish well here, etc. Included in re-analysis prompt."
      [minRows]="1">
    </app-resizable-textarea>
    <div class="notes-actions">
      <button mat-stroked-button (click)="saveNotes()" [disabled]="isSavingNotes">
        {{ isSavingNotes ? 'Saving…' : 'Save Notes' }}
      </button>
    </div>

    <!-- Visit History -->
    <h3 class="section-label">Visit History</h3>
    <table class="visit-table" *ngIf="spot?.dwell_events?.length">
      <thead>
        <tr><th>Trip</th><th>Date</th><th>Duration</th><th>Pings</th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let ev of spot?.dwell_events" class="visit-row" [routerLink]="['/trip', ev.trip_id]">
          <td>#{{ ev.trip_id }}</td>
          <td>{{ ev.start_time ? (ev.start_time | date:'mediumDate') : '—' }}</td>
          <td>{{ formatDuration(ev.duration_minutes) }}</td>
          <td>{{ ev.ping_count }}</td>
        </tr>
      </tbody>
    </table>
    <p *ngIf="!spot?.dwell_events?.length" class="stub-text">No visit history yet.</p>

    <!-- Catches at this spot -->
    <ng-container *ngIf="spotCatches.length > 0">
      <h3 class="section-label">Catches ({{ spotCatches.length }})</h3>
      <app-catch-table [features]="spotCatches"></app-catch-table>
    </ng-container>

    <!-- Actions -->
    <div class="action-row">
      <button mat-stroked-button (click)="buildMaps()" [disabled]="isBuildingMaps"
        title="Generate fresh satellite map images using the current center and radius">
        <span *ngIf="!isBuildingMaps">Build Maps</span>
        <span *ngIf="isBuildingMaps">Building…</span>
      </button>
      <button mat-stroked-button (click)="analyzePhysical()" [disabled]="isAnalyzingPhysical"
        title="Run AI analysis to identify physical features, depth, structure, and sub-zones">
        <span *ngIf="!isAnalyzingPhysical">{{ spot.location_analysis ? 'Re-analyze Maps' : 'Analyze Maps' }}</span>
        <span *ngIf="isAnalyzingPhysical">Analyzing…</span>
      </button>
      <button mat-stroked-button (click)="updateConditions()" [disabled]="isUpdatingConditions || isRebuildingConditions"
        title="Update conditions analysis incorporating any new catches since the last run">
        <span *ngIf="!isUpdatingConditions">Update Conditions</span>
        <span *ngIf="isUpdatingConditions">Updating…</span>
      </button>
      <button mat-stroked-button (click)="rebuildConditions()" [disabled]="isUpdatingConditions || isRebuildingConditions"
        title="Discard the previous analysis and rebuild from scratch using all catch data">
        <span *ngIf="!isRebuildingConditions">Rebuild Analysis</span>
        <span *ngIf="isRebuildingConditions">Rebuilding…</span>
      </button>
      <button mat-stroked-button class="delete-btn" (click)="deleteSpot()" [disabled]="isDeleting"
        title="Permanently delete this fishing spot and all associated dwell events">
        {{ isDeleting ? 'Deleting…' : 'Delete Spot' }}
      </button>
    </div>

    <!-- Conditions Analysis -->
    <ng-container *ngIf="spot.conditions_analysis">
      <h3 class="section-label">
        Conditions Analysis
        <ng-container *ngIf="spot.conditions_confidence !== null && spot.conditions_confidence !== undefined">
          <span class="confidence-score">{{ spot.conditions_confidence }}/10</span>
          <span class="confidence-bar">
            <span class="confidence-fill" [style.width.%]="spot.conditions_confidence * 10"></span>
          </span>
        </ng-container>
      </h3>
      <div class="conditions-md" [innerHTML]="spot.conditions_analysis | markdown"></div>
    </ng-container>

    <!-- Physical Analysis -->
    <ng-container *ngIf="spot.location_analysis">
      <h3 class="section-label">
        Physical Analysis
        <ng-container *ngIf="spot.location_analysis.confidence_score_0_10 !== null && spot.location_analysis.confidence_score_0_10 !== undefined">
          <span class="confidence-score">{{ spot.location_analysis.confidence_score_0_10 }}/10</span>
          <span class="confidence-bar">
            <span class="confidence-fill" [style.width.%]="spot.location_analysis.confidence_score_0_10 * 10"></span>
          </span>
        </ng-container>
      </h3>

      <dl class="spot-metadata physical-fields">
        <ng-container *ngIf="spot.location_analysis.named_location">
          <dt>Water body</dt><dd>{{ spot.location_analysis.named_location }}</dd>
        </ng-container>
        <ng-container *ngIf="spot.location_analysis.confirmed_feature">
          <dt>Feature</dt><dd>{{ spot.location_analysis.confirmed_feature }}</dd>
        </ng-container>
        <ng-container *ngIf="spot.location_analysis.material">
          <dt>Material</dt><dd>{{ spot.location_analysis.material }}</dd>
        </ng-container>
        <ng-container *ngIf="spot.location_analysis.depth_category">
          <dt>Depth</dt><dd>{{ spot.location_analysis.depth_category }}</dd>
        </ng-container>
        <ng-container *ngIf="spot.location_analysis.wind_exposure">
          <dt>Wind exposure</dt><dd>{{ spot.location_analysis.wind_exposure }}</dd>
        </ng-container>
        <ng-container *ngIf="spot.location_analysis.tidal_influence">
          <dt>Tidal influence</dt><dd>{{ spot.location_analysis.tidal_influence }}</dd>
        </ng-container>
      </dl>

      <div class="structure-elements" *ngIf="spot.location_analysis.structure_elements?.length">
        <span class="structure-tag" *ngFor="let el of spot.location_analysis.structure_elements">{{ el }}</span>
      </div>

      <p class="insight-text" *ngIf="spot.location_analysis.pattern_insight">
        {{ spot.location_analysis.pattern_insight }}
      </p>

      <ng-container *ngIf="spot.location_analysis.sub_zones?.length">
        <h4 class="sub-section-label">Sub-zones</h4>
        <table class="subzone-table">
          <thead>
            <tr><th>Position</th><th>Feature</th><th>Depth</th><th>Notes</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let zone of spot.location_analysis.sub_zones">
              <td class="zone-position">{{ zone.position }}</td>
              <td>{{ zone.feature }}</td>
              <td class="zone-depth">{{ zone.depth }}</td>
              <td class="zone-notes">{{ zone.notes }}</td>
            </tr>
          </tbody>
        </table>
      </ng-container>
    </ng-container>

  </section>
</div>

<!-- Lightbox -->
<div class="lightbox" *ngIf="lightboxUrl" (click)="closeLightbox()">
  <div class="lightbox-inner" (click)="$event.stopPropagation()">
    <button class="lightbox-close" (click)="closeLightbox()">✕</button>
    <p class="lightbox-label">{{ lightboxLabel }}</p>
    <img [src]="lightboxUrl" class="lightbox-img" />
  </div>
</div>

<app-loading-spinner [isLoading]="isLoading"></app-loading-spinner>

<div class="error-panel" *ngIf="!isLoading && errorMessage">
  <p>{{ errorMessage }}</p>
  <a routerLink="/spots">← Return to Fishing Spots</a>
</div>
